/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters */
// oxlint-disable typescript/no-explicit-any
import { HttpParams } from '@angular/common/http';

import { isObject } from './object-guards.functions';

import type { DeepRequired } from '@kp/types';

/**
 * Represents a callable value without using the unsafe global `Function` type.
 *
 * This type is only used to identify function-shaped values inside conditional
 * types. It does not describe a function that should be called directly.
 *
 * @example
 * ```ts
 * type Handler = () => string;
 * type IsCallable = Handler extends Callable ? true : false;
 * ```
 */
type Callable = (...args: Array<never>) => unknown;

type KeepInObjectCheck = (key: PropertyKeyValue, value: unknown) => boolean;

type PropertyKeyValue = number | string | symbol;

/**
 * Recursively removes selected value types from an object's properties.
 *
 * Arrays, dates, callables, and primitives are terminal values. Nested object
 * properties are transformed recursively.
 *
 * @example
 * ```ts
 * type Source = { name: string | null; nested: { count: number | null } };
 * type Result = RemoveTypes<Source, null>;
 * // Result is { name: string; nested: { count: number } }
 * ```
 */
type RemoveTypes<T, Removed> = {
  [
    Key in keyof T as Exclude<T[Key], Removed> extends never ? never : Key
  ]: Exclude<T[Key], Removed> extends Array<any>
    ? Exclude<T[Key], Removed>
    : Exclude<T[Key], Removed> extends
          | bigint
          | boolean
          | number
          | string
          | symbol
          | Callable
          | Date
      ? Exclude<T[Key], Removed>
      : Exclude<T[Key], Removed> extends object
        ? RemoveTypes<Exclude<T[Key], Removed>, Removed>
        : Exclude<T[Key], Removed>;
};

const RemoveNullFlag = {
  falsy: 2,
  null: 0,
  nullOrUndefined: 1,
} as const;

type RemoveNullFlagValue = (typeof RemoveNullFlag)[keyof typeof RemoveNullFlag];

type RemoveNullResult<T extends object> =
  | RemoveTypes<DeepRequired<T>, 0 | '' | null | undefined>
  | RemoveTypes<T, null>
  | RemoveTypes<T, null | undefined>;

function getFilteredValue(
  value: unknown,
  keepInObjCheckFn: KeepInObjectCheck,
): unknown {
  if (isObject(value)) {
    return removeFromObject(keepInObjCheckFn, value);
  }

  return Array.isArray(value)
    ? value.map(item =>
        isObject(item) ? removeFromObject(keepInObjCheckFn, item) : item,
      )
    : value;
}

/**
 * Removes nullish properties and creates Angular HTTP query parameters.
 *
 * @example
 * ```ts
 * objectToHttpParams({ query: 'Jane', page: null });
 * // serializes only the query parameter
 * ```
 */
function objectToHttpParams<T extends object>(object: T): HttpParams {
  const cleanedObject = removeNull<T>(object, RemoveNullFlag.nullOrUndefined);

  return new HttpParams({
    // HttpParams accepts a plain record shape that can't be derived from a generic T
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    fromObject: cleanedObject as any,
  });
}
/**
 * Recursively copies values accepted by the supplied predicate.
 *
 * Empty nested objects are removed after filtering. Plain objects inside arrays
 * are filtered recursively as well.
 *
 * @example
 * ```ts
 * const source = { name: 'Jane', age: null };
 * const result = removeFromObject((_key, value) => value !== null, source);
 * // result is { name: 'Jane' }
 * ```
 */
function removeFromObject<T extends object>(
  keepInObjCheckFn: KeepInObjectCheck,
  obj: T,
): Partial<T> {
  const retObject: Partial<T> = {};
  const entries = Object.entries(obj);

  for (const [key, value] of entries) {
    const updatedValue = getFilteredValue(value, keepInObjCheckFn);

    if (isObject(updatedValue) && Object.keys(updatedValue).length === 0) {
      continue;
    }

    if (keepInObjCheckFn(key, updatedValue)) {
      (retObject as Record<string, unknown>)[key] = updatedValue;
    }
  }

  return retObject;
}

/**
 * Removes null or falsy values recursively according to the selected mode.
 *
 * @example
 * ```ts
 * removeNull(
 *   { name: 'Jane', age: null },
 *   RemoveNullFlag.null,
 * );
 * // returns { name: 'Jane' }
 * ```
 */
function removeNull<T extends object>(
  obj: T,
  flag?: (typeof RemoveNullFlag)['null'],
): RemoveTypes<T, null>;
function removeNull<T extends object>(
  obj: T,
  flag: (typeof RemoveNullFlag)['nullOrUndefined'],
): RemoveTypes<T, null | undefined>;
function removeNull<T extends object>(
  obj: T,
  flag: (typeof RemoveNullFlag)['falsy'],
): RemoveTypes<DeepRequired<T>, 0 | '' | null | undefined>;
function removeNull<T extends object>(
  obj: T,
  flag: RemoveNullFlagValue,
): RemoveNullResult<T>;
function removeNull<T extends object>(
  obj: T,
  flag: RemoveNullFlagValue = RemoveNullFlag.null,
): unknown {
  const checkFn = (_key: PropertyKeyValue, value: unknown): boolean => {
    if (flag === RemoveNullFlag.falsy) {
      return Boolean(value);
    }

    return flag === RemoveNullFlag.nullOrUndefined
      ? value !== null && value !== undefined
      : value !== null;
  };

  return removeFromObject(checkFn, obj);
}

export { objectToHttpParams, removeFromObject, removeNull, RemoveNullFlag };
export type { RemoveNullFlagValue };
